import CreateEditCategoryModal from '@/components/CreateEditCategoryModal';
import { FadeInOutVariants, SlideXInOutVariants } from '@/constants/animations';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useAnimatePlanPages } from '@/contexts/AnimatedPlanPages';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { useHeaderHeight } from '@/hooks/useHeaderHeight';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import DashboardLayout from '@/layouts/Dashboard';
import FullCaptureListFAB from '@/pages/WeeklyCapturePage/Sections/FullCaptureListFAB';
import FullCaptureListOffset from '@/pages/WeeklyCapturePage/Sections/FullCaptureListOffset';
import LeftSection from '@/pages/WeeklyCapturePage/Sections/LeftSection';
import RightSection, { type CategoryWithActions } from '@/pages/WeeklyCapturePage/Sections/RightSection';
import { RoutesList } from '@/routes/routesList';
import { useActions } from '@/services/action/hooks';
import { useCategories } from '@/services/categories/hooks';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useUserInfo } from '@/services/user/hooks';
import { useActionsCategories } from '@/stores/useActionsCategories';
import { useCalendarMonthlyStore } from '@/stores/useCalendar';
import { HomeBackground } from '@/theme/StaticImages';
import { Category } from '@/types/category';
import { UserGroup } from '@/types/user';
import {
  getActionsByCategoryId,
  getCompletedActions,
  getDailyActions,
  getIncompleteActions,
  getWeeklyActions,
} from '@/utils/action';
import { getCategoryById, resolveCategoryImageByName } from '@/utils/category';
import { endOfTheWeek, startOfTheWeek } from '@/utils/calendar';
import {
  augmentCompletedActionsWithRecurringSeries,
  filterIncompleteActionsForRecurringWeekRange,
  mergeWeeklyRecurringCompletedShadows,
} from '@/utils/recurringCompletedShadow';
import { getColorFromToken } from '@/utils/color';
import { pageTransition } from '@/utils/pageAnimation';
import rem from '@/utils/rem';
import { trackCreateCategorySelected } from '@/utils/tracking';
import { Flex, SimpleGrid, VStack, useDisclosure } from '@chakra-ui/react';
import { motion, useAnimation } from 'framer-motion';
import { isEmpty, orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const blue1000 = getColorFromToken('blue.1000', '1');
const gray50 = getColorFromToken('gray.50', '1');
const blue1000_a50 = getColorFromToken('blue.1000', '0.50');

const MotionFlex = motion(Flex);

function WeeklyCapturePage() {
  const { t } = useTranslation('WeeklyCapturePage');
  const [showOffscreen, setShowOffscreen] = useState(false);
  const { pageNavigationEffect } = useAnimatePlanPages();
  const isDarkMode = useIsDarkMode();
  const { isEnabled: isNewCategorySpotlightEnabled } = useFeatureFlag(FEATURE_FLAGS.NEW_CATEGORY_SPOTLIGHT);
  const { isEnabled: isNewCategorySpotlightEnabledForBetaUsers } = useFeatureFlag(
    FEATURE_FLAGS.NEW_CATEGORY_SPOTLIGHT_FOR_BETA_USERS,
  );

  const { data: userInfo } = useUserInfo();
  const isBetaUser = userInfo?.me?.userGroup === UserGroup.BETA || userInfo?.me?.userGroup === UserGroup.ADMIN;

  const shouldShowNewCategorySpotlight =
    isNewCategorySpotlightEnabled || (isNewCategorySpotlightEnabledForBetaUsers && isBetaUser);
  const controlsAnimation = useAnimation();
  const navigate = useNavigate();

  const {
    isOpen: isCreateCategoryOpen,
    onOpen: onCreateCategoryOpen,
    onClose: onCreateCategoryClose,
  } = useDisclosure({ id: 'createNewCategory' });

  const selectedDate = useCalendarMonthlyStore((state) => state.selectedDate);
  const selectedCategory = useActionsCategories((state) => state.selectedCategory);
  const updateSelectedCategory = useActionsCategories((state) => state.updateSelectedCategory);
  const isDailyPlanPage = useIsDailyPlanPage();
  const [bgImageWidth, setBgImageWidth] = useState(0);
  const [bgImageHeight, setBgImageHeight] = useState(0);

  const { data: weeklyPlanId } = useWeeklyPlanId(selectedDate);
  const { data: categoriesList, isLoading: isCategoriesLoading } = useCategories();
  const { data: actions, isLoading: isLoadingActions } = useActions();

  const getSortedActionsByCategoryId = useCallback(
    (categoryId: string) => {
      if (!actions?.action) {
        return [];
      }

      const categoryActions = getActionsByCategoryId(actions.action, categoryId);

      const response = isDailyPlanPage
        ? getDailyActions(categoryActions, selectedDate)
        : getWeeklyActions(categoryActions, weeklyPlanId ?? '');

      return orderBy(response, 'categoryOrder', 'asc');
    },
    [actions?.action, selectedDate, weeklyPlanId, isDailyPlanPage],
  );

  const categories = useMemo(() => {
    if (!categoriesList?.category) {
      return [];
    }

    const fullCategoriesList = categoriesList.category
      .filter((c) => !c.isArchived)
      .map((category) => {
        const actions = getSortedActionsByCategoryId(category.id);

        let incompleteActionsFiltered = getIncompleteActions(actions, 'categoryOrder').filter(
          (a) => !a.project?.isArchived && !a.category.isArchived && !a.isCanceled,
        );
        let completedActionsFiltered = getCompletedActions(actions, 'modifiedAt').filter(
          (a) => !a.project?.isArchived && !a.category.isArchived && !a.isCanceled,
        );

        if (!isDailyPlanPage) {
          const rangeStart = startOfTheWeek(selectedDate);
          const rangeEnd = endOfTheWeek(selectedDate);
          incompleteActionsFiltered = filterIncompleteActionsForRecurringWeekRange(
            incompleteActionsFiltered,
            rangeStart,
            rangeEnd,
          );
          completedActionsFiltered = mergeWeeklyRecurringCompletedShadows(
            incompleteActionsFiltered,
            completedActionsFiltered,
            actions,
            rangeStart,
            rangeEnd,
          );
        } else {
          completedActionsFiltered = augmentCompletedActionsWithRecurringSeries(
            incompleteActionsFiltered,
            completedActionsFiltered,
          );
        }

        return {
          ...category,
          incompleteActions: incompleteActionsFiltered,
          completedActions: completedActionsFiltered,
        };
      });
    // Return 'uncategorized' if filteredCategories is empty
    if (fullCategoriesList.length === 0 && !isEmpty(categoriesList?.category)) {
      const getUncategorized = categoriesList.category.find(
        (el) => el.name.toLowerCase() === 'uncategorized',
      ) as Category;

      return [
        {
          ...getUncategorized,
          incompleteActions: [],
          completedActions: [],
        },
      ];
    }

    return fullCategoriesList;
  }, [categoriesList?.category, getSortedActionsByCategoryId, selectedDate, isDailyPlanPage]);

  const category = useMemo((): CategoryWithActions | undefined => {
    if (selectedCategory?.id) {
      return getCategoryById(categories, selectedCategory?.id) as CategoryWithActions | undefined;
    }
    return categories?.find((c) => c.name.toLowerCase() === 'uncategorized') as CategoryWithActions | undefined;
  }, [categories, selectedCategory?.id]);

  const resetCategorySelected = useCallback(() => {
    updateSelectedCategory(categories?.[0]);
  }, [categories, updateSelectedCategory]);

  const toggleOffscreen = useCallback(() => {
    setShowOffscreen((prevState) => !prevState);
  }, []);

  useEffect(() => {
    controlsAnimation?.start('visible');
  }, [showOffscreen, controlsAnimation]);

  useEffect(() => {
    if (categories?.find((el) => el?.id === selectedCategory?.id) === undefined && showOffscreen) {
      resetCategorySelected();
    }

    if (categories?.length > 0 && !isCategoriesLoading && !selectedCategory) {
      resetCategorySelected();
    }
  }, [categories, isCategoriesLoading, resetCategorySelected, selectedCategory, showOffscreen]);

  useEffect(() => {
    return () => {
      if (!shouldShowNewCategorySpotlight) {
        updateSelectedCategory(null);
      }
    };
  }, [updateSelectedCategory, shouldShowNewCategorySpotlight]);

  useEffect(() => {
    setBgImageWidth(window.innerWidth);
    setBgImageHeight(Math.ceil(window.innerHeight * 0.7));
  }, []);

  const categoryImageUrl = `${category?.selectedImageUrl}${category?.selectedImageUrl ? `&w=${bgImageWidth}&dpr=2&q=37` : ''}`;
  const backgroundImageUrl =
    bgImageWidth && bgImageHeight && category?.selectedImageUrl
      ? resolveCategoryImageByName(category?.selectedImageUrl) +
        (category?.selectedImageUrl.includes('images.unsplash.com')
          ? `&w=${bgImageWidth}&h=${bgImageHeight}&fit=crop&crop=faces%2Cedges&dpr=2&q=37`
          : '')
      : '';

  const headerHeight = useHeaderHeight();

  return (
    <DashboardLayout pageTitle={t('pageTitle')} padding="0" position="relative" forceShowSecondaryNav={showOffscreen}>
      <motion.div initial="initial" animate="in" exit="out" variants={pageNavigationEffect} transition={pageTransition}>
        <FullCaptureListOffset isVisible={!showOffscreen} />

        {showOffscreen && (
          <>
            <MotionFlex
              position="fixed"
              key={categoryImageUrl} // Important for triggering animation on change
              variants={FadeInOutVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              transition={{ duration: 0.5 }}
              width="full"
              height={`calc(100vh - ${rem(headerHeight)})`}
              background={`linear-gradient(0deg, ${isDarkMode ? blue1000 : gray50} 0%, ${blue1000_a50} 100%), url(${
                backgroundImageUrl ?? HomeBackground
              }), lightgray 0px -257.529px / 122.578% 155.719% no-repeat`}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
              backgroundColor="blue.1000"
            />
            <Flex
              position="relative"
              overflow="auto"
              overflowX="hidden"
              height="full"
              paddingTop={rem(152)}
              paddingX={rem(16)}
            >
              <SimpleGrid gap={rem(90)} width="full" columns={2} paddingX={rem(12)}>
                {/* Left column */}
                <VStack alignItems="start" gap={rem(21)} width="full">
                  <LeftSection
                    categories={categories.filter((c) => !c.isArchived)}
                    isCategoriesLoading={isCategoriesLoading}
                    controlsAnimation={controlsAnimation}
                    onCreateCategoryOpen={() => {
                      onCreateCategoryOpen();
                      trackCreateCategorySelected('planner');
                    }}
                  />
                </VStack>

                {/* Right column */}
                <motion.div
                  initial="hidden"
                  animate={controlsAnimation}
                  variants={SlideXInOutVariants}
                  transition={{ duration: 0.2 }}
                  style={{
                    marginBottom: rem(80),
                  }}
                >
                  <VStack alignItems="start" gap={rem(16)} width="full">
                    <RightSection
                      category={category}
                      actions={actions}
                      isCategoriesLoading={isCategoriesLoading}
                      isLoadingActions={isLoadingActions}
                    />
                  </VStack>
                </motion.div>
              </SimpleGrid>
            </Flex>
          </>
        )}

        <FullCaptureListFAB
          onClick={
            shouldShowNewCategorySpotlight
              ? () => {
                  navigate(RoutesList.CategorySpotlightPage);
                }
              : toggleOffscreen
          }
          buttonProps={{
            left: '45%',
            translateX: '-45%',
          }}
          innerText={showOffscreen ? t('buttons.viewFullCategoryList') : t('buttons.openCategorySpotlight')}
          chevronLeft={showOffscreen}
        />

        {isCreateCategoryOpen && (
          <CreateEditCategoryModal isOpen={isCreateCategoryOpen} onClose={onCreateCategoryClose} />
        )}
      </motion.div>
    </DashboardLayout>
  );
}

export default WeeklyCapturePage;
